/**
 * 캘린더 모듈 Context
 *
 * - 현재 워크스페이스의 일정 목록 (현재 활성 range)
 * - 활성 뷰 모드 (월/주/일/어젠다)
 * - 필터 토글 상태
 * - CRUD 액션 래퍼
 *
 * 합의 문서: .agent/plans/calendar_phase1_design.md
 */
import { createContext, useContext } from 'react';
import { CalendarEvent, CalendarViewMode, CalendarFilters, DEFAULT_FILTERS } from '../models/CalendarEvent';

/** 활성 표시 range — 뷰에 따라 자동 계산 */
export interface DateRange {
  start: Date;
  end: Date;
}

export interface ICalendarContext {
  /** 현재 뷰 모드 */
  viewMode: CalendarViewMode;
  setViewMode: (mode: CalendarViewMode) => void;

  /** 활성 표시 범위 (뷰 + 기준일로 자동 계산) */
  range: DateRange;
  setRange: (range: DateRange) => void;

  /** 활성 기준일 (월/주/일 네비게이션의 anchor) */
  anchorDate: Date;
  setAnchorDate: (date: Date) => void;

  /** 일정 목록 */
  events: CalendarEvent[];
  setEvents: (events: CalendarEvent[]) => void;

  /** Tasks 마감일 가상 일정 (Step 13 — 메인 캘린더용) */
  taskEvents: CalendarEvent[];

  /** 내 per-user 리마인더 맵 (event_id → minutes[]) */
  remindersMap: Record<string, number[]>;

  /** 현재 선택된 일정 (상세 모달용) */
  selectedEvent: CalendarEvent | null;
  setSelectedEvent: (event: CalendarEvent | null) => void;

  /** 필터 토글 */
  filters: CalendarFilters;
  setFilters: (filters: CalendarFilters) => void;

  /** 로딩 상태 */
  loading: boolean;
  setLoading: (b: boolean) => void;

  /** 일정 목록 새로고침 */
  refreshEvents: () => Promise<void>;

  /** 내가 attendee 인 event_id 집합 (declined 제외) — "초대받은 일정" 필터용 */
  attendingEventIds: Set<string>;

  /** 에러 상태 */
  error: string | null;
}

export const CalendarContext = createContext<ICalendarContext>({
  viewMode: 'month',
  setViewMode: () => {},
  range: { start: new Date(), end: new Date() },
  setRange: () => {},
  anchorDate: new Date(),
  setAnchorDate: () => {},
  events: [],
  setEvents: () => {},
  taskEvents: [],
  remindersMap: {},
  selectedEvent: null,
  setSelectedEvent: () => {},
  filters: DEFAULT_FILTERS,
  setFilters: () => {},
  loading: false,
  setLoading: () => {},
  refreshEvents: async () => {},
  attendingEventIds: new Set<string>(),
  error: null,
});

export const useCalendarContext = () => useContext(CalendarContext);
